/**
 * Plugin Integration Mixin
 * 
 * Provides common methods for integrating with the Plugin System.
 * Can be used in any component that needs to support plugin content overrides,
 * hooks, and dynamic tabs.
 */

export default {
  methods: {
    // Plugin Hook: Get component for plugin-registered tab
    getPluginTabComponent(tabKey) {
      const pluginSystem =
        this.$root?.$options?.config?.globalProperties?.$pluginSystem ||
        window.PluginSystem;

      if (!pluginSystem) return null;
      
      const context = this.getPluginContext ? this.getPluginContext() : this.inbox || {};
      const pluginTabs = pluginSystem.getTabs(this.pluginComponentName || 'Settings', context);
      const tab = pluginTabs.find(t => t.key === tabKey);
      return tab?.component || null;
    },
    
    // Plugin Hook: Get content override for existing tab
    getPluginContentOverride(tabKey) {
      const pluginSystem =
        this.$root?.$options?.config?.globalProperties?.$pluginSystem ||
        window.PluginSystem;

      if (!pluginSystem) return null;
      
      const context = this.getPluginContext ? this.getPluginContext() : this.inbox || {};
      const componentName = this.pluginComponentName || 'Settings';
      
      // Check if any plugin wants to override this tab's content
      const override = pluginSystem.getContentOverride 
        ? pluginSystem.getContentOverride(componentName, tabKey, context)
        : null;
        
      return override;
    },
    
    // Plugin Hook: Check if tab content should be overridden
    shouldOverrideTabContent(tabKey) {
      const override = this.getPluginContentOverride(tabKey);
      return !!override;
    },

    // Plugin Hook: Execute plugin hooks with context
    executePluginHook(hookName, data) {
      const pluginSystem =
        this.$root?.$options?.config?.globalProperties?.$pluginSystem ||
        window.PluginSystem;
        
      if (!pluginSystem?.executeHook) return null;
      
      const context = this.getPluginContext ? this.getPluginContext() : this.inbox || {};
      
      return pluginSystem.executeHook(hookName, {
        ...data,
        context: this,
        pluginContext: context
      });
    },

    // Plugin Hook: Get plugin-registered tabs
    getPluginTabs() {
      const pluginSystem =
        this.$root?.$options?.config?.globalProperties?.$pluginSystem ||
        window.PluginSystem;

      if (!pluginSystem) return [];
      
      const context = this.getPluginContext ? this.getPluginContext() : this.inbox || {};
      const componentName = this.pluginComponentName || 'Settings';
      
      return pluginSystem.getTabs(componentName, context);
    },

    // Plugin Hook: Check if Plugin System is available
    hasPluginSystem() {
      const pluginSystem =
        this.$root?.$options?.config?.globalProperties?.$pluginSystem ||
        window.PluginSystem;
        
      return !!pluginSystem;
    },

    // Utility: Safe check for plugin functionality
    executePluginMethod(methodName, ...args) {
      const pluginSystem =
        this.$root?.$options?.config?.globalProperties?.$pluginSystem ||
        window.PluginSystem;

      if (!pluginSystem || !pluginSystem[methodName]) return null;
      
      try {
        return pluginSystem[methodName](...args);
      } catch (error) {
        console.warn(`[PLUGIN-MIXIN] Error executing ${methodName}:`, error);
        return null;
      }
    },

    // Default plugin context (can be overridden in components)
    getPluginContext() {
      return {
        componentName: this.pluginComponentName || 'Unknown',
        inbox: this.inbox,
        route: this.$route,
        user: this.$store?.getters?.getCurrentUser
      };
    }
  }
};